// ============================================================
// Die Abfragen und Änderungen von P0 (SPEC §13.4): Anmeldung, Ich, Konfig,
// Nutzerverwaltung. Die Fachbereiche haben ihre eigenen Dateien (katalog.ts P5,
// modell.ts P6, bibliothek.ts/ki.ts/anreicherung.ts P7).
//
// Zwei Arten von Exporten:
//  - nackte Aufrufe (`holeMich`, `codeAnfordern` …) — für Stellen außerhalb von
//    React (der Anmeldespeicher, `fetchQuery` nach dem Einlösen);
//  - Haken (`useIch`, `useNutzerAendern` …) für die Bildschirme.
// ============================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";
import { schluessel } from "../schluessel";
import type { AdminNutzer, AuthConfig, AuthStart, AuthVerify, Ich, IchAendern, Konfig, NutzerAendern, NutzerNeu } from "../types";

/* ------------------------------------------------------------- nackte Aufrufe */

export const holeAuthConfig = () => api.get<AuthConfig>("/auth/config");
export const codeAnfordern = (email: string, turnstile: string | null) =>
  api.post<AuthStart>("/auth/start", { email, turnstile: turnstile ?? "" });
export const codeEinloesen = (challenge: string, code: string, angemeldet_bleiben: boolean) =>
  api.post<AuthVerify>("/auth/verify", { challenge, code, angemeldet_bleiben });
export const abmelden = () => api.post<{ ok: boolean }>("/auth/logout");
export const holeMich = () => api.get<Ich>("/me");
export const holeKonfig = () => api.get<Konfig>("/konfig");

/* ----------------------------------------------------------------- Ich, Konfig */

/**
 * `GET /me` — DER eine Aufruf, aus dem App.tsx den Anmeldestatus ableitet und aus dem
 * jeder Bildschirm liest. `aktiv=false` nach dem Abmelden: dann wird nicht gefragt.
 */
export function useIch(aktiv = true) {
  return useQuery({
    queryKey: schluessel.ich,
    queryFn: holeMich,
    enabled: aktiv,
    staleTime: 60_000,
    retry: false,
  });
}

export function useIchAendern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rumpf: IchAendern) => api.patch<Ich>("/me", rumpf),
    onSuccess: (ich) => qc.setQueryData(schluessel.ich, ich),
  });
}

export function useKonfig(aktiv = true) {
  return useQuery({ queryKey: schluessel.konfig, queryFn: holeKonfig, enabled: aktiv, staleTime: 5 * 60_000 });
}

/* ------------------------------------------------------------ Nutzerverwaltung */

export function useNutzerListe() {
  return useQuery({ queryKey: schluessel.nutzer, queryFn: () => api.get<AdminNutzer[]>("/admin/nutzer") });
}

/** Eine geänderte Zeile in die Liste schreiben, statt die ganze Liste neu zu holen. */
function zeileErsetzen(liste: AdminNutzer[] | undefined, neu: AdminNutzer): AdminNutzer[] | undefined {
  if (!liste) return liste;
  const da = liste.some((z) => z.id === neu.id);
  return da ? liste.map((z) => (z.id === neu.id ? neu : z)) : [...liste, neu];
}

export function useNutzerAnlegen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rumpf: NutzerNeu) => api.post<AdminNutzer>("/admin/nutzer", rumpf),
    onSuccess: (neu) => qc.setQueryData<AdminNutzer[]>(schluessel.nutzer, (alt) => zeileErsetzen(alt, neu)),
  });
}

export function useNutzerAendern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...rumpf }: NutzerAendern & { id: string }) => api.patch<AdminNutzer>(`/admin/nutzer/${id}`, rumpf),
    onSuccess: (neu) => qc.setQueryData<AdminNutzer[]>(schluessel.nutzer, (alt) => zeileErsetzen(alt, neu)),
  });
}

export function useEntsperren() {
  return useMutation({
    mutationFn: (email: string) => api.post<{ geloescht: number }>("/admin/entsperren", { email }),
  });
}
